/**
 * metaPixel — Meta (Facebook) Pixel לדף התרומה הציבורי.
 *
 * שום מזהה לא מקודד כאן. הפיקסל נפתר בזמן ריצה בשרשרת:
 *
 *   1. campaign.facebookPixelId  — פיקסל של הקמפיין הספציפי
 *   2. organization.facebookPixelId — ברירת מחדל לכל קמפייני העמותה,
 *      משמשת כשלקמפיין אין משלו
 *   3. ambassador.facebookPixelId — פיקסל נוסף (לא במקום), נטען רק כשהתורם
 *      הגיע דרך ?ref= של אותו שגריר. כך שגריר שמריץ מודעות מחשבון המודעות
 *      שלו מודד את התנועה שלו, והעמותה ממשיכה למדוד את הכל.
 *
 * אפשר לטעון כמה פיקסלים במקביל, ולכן כל אירוע נשלח ב-trackSingle לכל
 * מזהה בנפרד — track רגיל היה משדר לכולם ומערבב מדידות של גופים שונים.
 *
 * קמפיין בלי אף מזהה בשרשרת לא טוען שום סקריפט מעקב — זו ברירת המחדל של
 * כל קמפיין קיים.
 *
 * למה הכל ידני: מסע התורם חוצה שני מסלולי SPA
 * (/campaign/:slug → /payments/complete) וטופס האשראי עצמו יושב ב-iframe
 * של ספק הסליקה. אין אף טעינת דף אמיתית בין "אני תורם" לאישור החיוב, ולכן
 * שום אירוע לא נורה מעצמו — כל track() כאן נקרא בפירוש מתוך הקוד.
 *
 * Purchase נורה אך ורק ב-/payments/complete, אחרי שהסליקה החזירה הצלחה.
 * לירות אותו על לחיצת "שלח" היה מלמד את מטא לחפש נוטשים — בדיוק ההפך
 * מהאופטימיזציה שאנחנו רוצים.
 */

declare global {
  interface Window {
    fbq?: ((...args: any[]) => void) & { callMethod?: (...args: any[]) => void; queue?: any[]; loaded?: boolean; version?: string };
    _fbq?: unknown;
  }
}

/** מזהה פיקסל תקין = ספרות בלבד. חוסם הדבקה של URL/קוד שלם לשדה. */
const isValidPixelId = (id: string): boolean => /^\d{5,20}$/.test(id);

/** הפיקסלים שכבר אותחלו בטעינת העמוד הזו, לפי סדר האתחול. */
let activePixelIds: string[] = [];

/**
 * יומן אירועים לניפוי בשטח. אין דרך אחרת לענות על "האם האירוע ירה?" בדף
 * ייצור: Pixel Helper מפספס אירועים שנורים רגע לפני ניווט או פתיחת iframe,
 * ומטא מציגה אותם בעיכוב. כאן רואים בקונסול בדיוק מה נשלח ומתי:
 *
 *   __amutechPixel.log      → כל האירועים שנורו בטעינה הזו
 *   __amutechPixel.ids      → הפיקסלים הפעילים
 *
 * נשמר בזיכרון בלבד (30 אחרונים), בלי פרטי תורם.
 */
const eventLog: Array<{ at: string; event: string; pixelIds: string[]; params?: Record<string, unknown> }> = [];

const exposeDebug = (): void => {
  if (typeof window === 'undefined') return;
  (window as any).__amutechPixel = {
    get ids() { return [...activePixelIds]; },
    get log() { return [...eventLog]; },
  };
};

/** רשימת הפיקסלים הפעילים כרגע. */
export function getActivePixelIds(): string[] {
  return [...activePixelIds];
}

/**
 * טוען את קוד הבסיס של מטא ומאתחל כל מזהה שעוד לא אותחל. אידמפוטנטי —
 * קריאה חוזרת עם אותם מזהים לא עושה כלום, ולכן בטוח לקרוא מתוך useEffect.
 *
 * מקבל מזהה בודד או רשימה (קמפיין/עמותה + שגריר). ערכים ריקים ולא תקינים
 * נופלים בשקט, כך שאפשר להעביר ישירות שדות שאולי לא הוגדרו.
 *
 * מחזיר את רשימת הפיקסלים הפעילים אחרי הקריאה.
 */
export function initMetaPixel(pixelIds?: string | string[] | null): string[] {
  if (typeof window === 'undefined') return [];

  const wanted = (Array.isArray(pixelIds) ? pixelIds : [pixelIds])
    .map((v) => (v || '').trim())
    .filter(Boolean)
    .filter((id) => {
      if (isValidPixelId(id)) return true;
      console.warn('[metaPixel] מזהה פיקסל לא תקין, המעקב לא הופעל:', id);
      return false;
    });

  // מזהה כפול (למשל שגריר שהוגדר לו אותו פיקסל כמו הקמפיין) יאותחל פעם
  // אחת בלבד, אחרת כל אירוע היה נשלח אליו פעמיים.
  const fresh = [...new Set(wanted)].filter((id) => !activePixelIds.includes(id));
  if (!fresh.length) return getActivePixelIds();

  // snippet הרשמי של מטא. המזהה עצמו לעולם לא מוזרק לתוך הסקריפט — הוא
  // מועבר כארגומנט ל-fbq() אחרי הטעינה, כך שערך מהמסד לא יכול להפוך לקוד
  // רץ בדף.
  //
  // הכל עטוף ב-try: כלי מדידה לא מפיל דף תרומה. אם טעינת הפיקסל נכשלת
  // מסיבה כלשהי, התורם ממשיך לתרום ואנחנו פשוט לא מודדים.
  try {
    if (!window.fbq) {
      /* eslint-disable */
      (function (f: any, b: Document, e: string, v: string) {
        if (f.fbq) return;
        const n: any = (f.fbq = function () {
          n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments);
        });
        if (!f._fbq) f._fbq = n;
        n.push = n;
        n.loaded = true;
        n.version = '2.0';
        n.queue = [];
        const t = b.createElement(e) as HTMLScriptElement;
        t.async = true;
        t.src = v;
        // מטא מזריקים לפני ה-script הראשון בדף. כשאין כזה (דף שנבנה
        // דינמית, סביבת בדיקות) ה-snippet המקורי נופל על undefined —
        // ואז נופל איתו כל ה-effect שקרא לו. head הוא נפילה רכה.
        const s = b.getElementsByTagName(e)[0];
        if (s?.parentNode) s.parentNode.insertBefore(t, s);
        else (b.head || b.documentElement).appendChild(t);
      })(window, document, 'script', 'https://connect.facebook.net/en_US/fbevents.js');
      /* eslint-enable */
    }

    for (const id of fresh) {
      window.fbq?.('init', id);
      activePixelIds.push(id);
    }
    exposeDebug();
    return getActivePixelIds();
  } catch {
    return getActivePixelIds();
  }
}

/**
 * שולח אירוע לכל פיקסל פעיל. no-op שקט כשאין אף פיקסל, כך שכל קריאה
 * בטוחה ללא תנאי.
 *
 * trackSingle ולא track: כשטעונים גם פיקסל של העמותה וגם של שגריר, track
 * היה משדר כל אירוע לשניהם בלי הבחנה. כאן כל אירוע נשלח במפורש לכל מזהה.
 */
export function pixelTrack(
  event: string,
  params?: Record<string, unknown>,
  eventId?: string,
  pixelIds?: string[],
): void {
  if (typeof window === 'undefined') return;

  // היעדים: מה שהקורא נקב בו במפורש, ורק אם לא נקב — הרשימה הפנימית.
  //
  // למה בכלל לאפשר לנקוב: נקודות הירי קוראות ל-initMetaPixel ומקבלות ממנו
  // רשימה, ואז קראו לכאן. אם הרשימה הפנימית הייתה ריקה באותו רגע — מכל
  // סיבה — האירוע נבלע בשקט, למרות שהקורא החזיק ביד רשימה תקינה. זה בדיוק
  // מה שקרה ל-InitiateCheckout בפרוד: ההפקדה ל-sessionStorage נשמרה עם
  // pixelIds מלאים, והשליחה שרצה שורה אחריה לא שלחה כלום.
  const targets = pixelIds?.length ? [...new Set(pixelIds)] : activePixelIds;
  if (!targets.length) return;

  // fbq עשוי להיעדר אם הסקריפט לא הספיק להיטען או נוקה. אתחול חוזר
  // אידמפוטנטי מחזיר אותו; אם גם אז אין — אין למי לשלוח.
  if (!window.fbq) initMetaPixel(targets);
  if (!window.fbq) return;

  try {
    for (const id of targets) {
      // eventID מאפשר למטא לאחד את האירוע הזה עם אותו אירוע שיישלח בעתיד
      // מהשרת (Conversions API) במקום לספור אותו פעמיים.
      if (eventId) window.fbq('trackSingle', id, event, params || {}, { eventID: eventId });
      else window.fbq('trackSingle', id, event, params || {});
    }
    eventLog.push({ at: new Date().toISOString(), event, pixelIds: [...targets], params });
    if (eventLog.length > 30) eventLog.shift();
    exposeDebug();
  } catch {
    /* חסימת פרסום/הרחבת דפדפן — המעקב נופל, התרומה ממשיכה */
  }
}


// ── גשר בין דף הקמפיין לדף התודה ────────────────────────────────────────────
// /payments/complete הוא מסלול נפרד שלא יודע לאיזה קמפיין הוא שייך: הוא
// מקבל רק amount ו-tx בשורת הכתובת. לכן דף הקמפיין מפקיד כאן את מזהה
// הפיקסל והמטבע רגע לפני שהוא פותח את הסליקה, ודף התודה אוסף אותם.
// sessionStorage ולא state — בין השניים יש ניווט מלא של הדפדפן.

const PENDING_KEY = 'metaPixelPendingPurchase';

export interface PendingPurchase {
  /** כל הפיקסלים שהיו פעילים בדף הקמפיין — עמותה/קמפיין ושגריר. */
  pixelIds: string[];
  /** הסכום שהתורם בחר. הסכום שהסליקה אישרה גובר עליו אם הוחזר. */
  value: number;
  currency: string;
  campaignId?: string;
  campaignName?: string;
  /** slug השגריר (?ref=) — מאפשר לפלח ביצועים לפי שגריר במטא. */
  ambassadorSlug?: string;
}

export function stashPendingPurchase(p: PendingPurchase): void {
  try {
    sessionStorage.setItem(PENDING_KEY, JSON.stringify(p));
  } catch {
    /* מצב פרטי / אחסון מלא — נאבד רק את המעקב */
  }
}

/** קורא ומוחק את ההפקדה. המחיקה מונעת ירי חוזר על רענון דף התודה. */
export function consumePendingPurchase(): PendingPurchase | null {
  try {
    const raw = sessionStorage.getItem(PENDING_KEY);
    if (!raw) return null;
    sessionStorage.removeItem(PENDING_KEY);
    const parsed = JSON.parse(raw);
    if (!parsed || !Array.isArray(parsed.pixelIds) || !parsed.pixelIds.length) return null;
    return parsed as PendingPurchase;
  } catch {
    return null;
  }
}

/**
 * true בפעם הראשונה שרואים את מזהה העסקה הזה, false אחר כך.
 *
 * חוזר מ"תודה" עם כפתור הדפדפן, שיתוף הקישור, רענון — כולם טוענים מחדש
 * את אותו /payments/complete. בלי הבלימה הזו כל אחד מהם היה נספר כתרומה
 * נוספת ומנפח את ההמרות במטא.
 */
export function claimPurchaseOnce(txId: string): boolean {
  const key = `metaPixelPurchaseFired:${txId || 'no-tx'}`;
  try {
    if (sessionStorage.getItem(key)) return false;
    sessionStorage.setItem(key, '1');
    return true;
  } catch {
    return true;
  }
}
